import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface BrandProductTag {
  id: string;
  brand_id: string;
  name: string;
  color: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export const useBrandProductTags = (brandId: string) => {
  const queryClient = useQueryClient();

  const {
    data: tags,
    isLoading,
    error
  } = useQuery({
    queryKey: ['brand-product-tags', brandId],
    queryFn: async (): Promise<BrandProductTag[]> => {
      const { data, error } = await supabase
        .from('brand_product_tags')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!brandId,
  });

  const createTagMutation = useMutation({
    mutationFn: async (tagData: Omit<BrandProductTag, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('brand_product_tags')
        .insert(tagData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-tags', brandId] });
      toast({
        title: "Success",
        description: "Tag created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating tag:', error);
      toast({
        title: "Error",
        description: "Failed to create tag",
        variant: "destructive",
      });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<BrandProductTag> & { id: string }) => {
      const { data, error } = await supabase
        .from('brand_product_tags')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-tags', brandId] });
      toast({
        title: "Success",
        description: "Tag updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating tag:', error);
      toast({
        title: "Error",
        description: "Failed to update tag",
        variant: "destructive",
      });
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: async (tagId: string) => {
      // First, get all products that have this tag
      const { data: products, error: fetchError } = await supabase
        .from('brand_products_hooks')
        .select('id, tags')
        .eq('brand_id', brandId);

      if (fetchError) throw fetchError;

      // Update each product to remove the tag
      for (const product of products || []) {
        let currentTags: string[] = [];
        
        // Parse existing tags
        if (Array.isArray(product.tags)) {
          currentTags = (product.tags as any[]).map(String);
        } else if (typeof product.tags === 'string') {
          try {
            const parsed = JSON.parse(product.tags);
            currentTags = Array.isArray(parsed) ? parsed.map(String) : [];
          } catch {
            currentTags = [];
          }
        }

        // Remove the tag if it exists
        if (currentTags.includes(tagId)) {
          const updatedTags = currentTags.filter(id => id !== tagId);
          
          const { error: updateError } = await supabase
            .from('brand_products_hooks')
            .update({ tags: JSON.stringify(updatedTags) })
            .eq('id', product.id);

          if (updateError) throw updateError;
        }
      }

      // Then delete the tag
      const { error } = await supabase
        .from('brand_product_tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-tags', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand-products-enhanced', brandId] });
      toast({
        title: "Success",
        description: "Tag deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive",
      });
    },
  });

  const createTag = (tagData: Omit<BrandProductTag, 'id' | 'created_at' | 'updated_at'>) => {
    createTagMutation.mutate(tagData);
  };

  const updateTag = (id: string, updates: Partial<BrandProductTag>) => {
    updateTagMutation.mutate({ id, ...updates });
  };

  const deleteTag = (id: string) => {
    deleteTagMutation.mutate(id);
  };

  return {
    tags: tags || [],
    isLoading,
    error,
    createTag,
    updateTag,
    deleteTag,
    isCreating: createTagMutation.isPending,
    isUpdating: updateTagMutation.isPending,
    isDeleting: deleteTagMutation.isPending,
  };
};